import { Component, HostListener, Input, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonV3Component, IconV3Component, PageHeaderModule } from '@gea/digital-ui-lib';
import {
  ColumnDefinitionV3,
  ColumnRendererComponentV3,
  PageContentLayoutComponent,
  SelectFilterRendererV3,
  SelectFilterV3Component,
  SearchBarV2Module,
  TableServiceV3,
  TableV3Component,
  TextFilterV3Component,
} from '@gea/digital-ui-lib';
import { DndFileDirective } from 'src/app/shared/directives/dnd-file.directive';
import { RenderFileIcon } from '../document-library/document-renderers/file-icon-renderer.component';
import { Category, CategoryDescriptions, Department, DepartmentDescriptions } from 'src/app/shared/data/engineering-enums';
import { ApplicationFeature } from 'src/app/shared/data/application-feature';
import { UserDocument, UserDocumentView } from 'src/app/shared/data/data.generated';
import { ApiService } from 'src/app/shared/services/api.service';
import {
  ActiveChipFilters,
  Chip,
  HrtFilterChipsComponent,
} from 'src/app/shared/components/hrt-filter-chips/hrt-filter-chips.component';

interface RepDocumentsCellRendererConfig {
  type: 'description' | 'actions';
  canEdit?: boolean;
  onDownload?: (row: any) => void;
  onEdit?: (row: any) => void;
}

@Component({
  selector: 'app-rep-documents-cell-renderer',
  template: `
    <span *ngIf="config.type === 'description'" class="description-cell-content">
      {{ rowData?.Description }}
    </span>
    <span *ngIf="config.type === 'actions'" class="document-actions">
      <geaui-icon-v3
        [attr.aria-label]="'Download ' + rowData.Title"
        title="View / Download"
        file="16px_download"
        [clickable]="true"
        (click)="config.onDownload?.(rowData)"
      ></geaui-icon-v3>
      <geaui-icon-v3
        *ngIf="config.canEdit"
        [attr.aria-label]="'Edit ' + rowData.Title"
        title="Edit document"
        file="16px_edit"
        [clickable]="true"
        (click)="config.onEdit?.(rowData)"
      ></geaui-icon-v3>
    </span>
  `,
  styles: [`
    .description-cell-content {
      display: block;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      white-space: normal;
      overflow-wrap: anywhere;
    }

    geaui-icon-v3 {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
    }

    .document-actions {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
  `],
  standalone: true,
  imports: [CommonModule, IconV3Component],
})
export class RepDocumentsCellRenderer {
  @Input() rowData: any;
  @Input() config: RepDocumentsCellRendererConfig = { type: 'description' };
}

@Component({
  selector: 'app-rep-documents',
  templateUrl: './rep-documents.component.html',
  styleUrls: ['./rep-documents.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    PageHeaderModule,
    PageContentLayoutComponent,
    SearchBarV2Module,
    TableV3Component,
    HrtFilterChipsComponent,
    ButtonV3Component,
    DndFileDirective,
  ],
})
export class RepDocumentsComponent {
  constructor(
    private readonly api: ApiService,
    private readonly tableService: TableServiceV3,
    public dialog: MatDialog,
    formBuilder: FormBuilder,
  ) {
    this.searchForm = formBuilder.group({ search: [''] });
  }

  documents: UserDocumentView[] = [];
  readonly tableId = 'hrtc-rep-info-table';
  tableRows: any[] = [];
  totalRows = 0;
  private page = 0;
  private pageSize = 10;
  columnDefinitions: ColumnDefinitionV3[] = [];
  searchText = '';
  activeChipFilters: ActiveChipFilters = { searchText: '', filters: {} };
  showArchived = false;
  searchForm: FormGroup;
  draft = new UserDocumentView();
  selectedFile: File | null = null;
  isSaving = false;
  isDocumentDialogOpen = false;
  pageMessage = '';
  dialogMessage = '';
  private dialogRef: MatDialogRef<unknown> | null = null;
  @ViewChild('documentDialog') documentDialog!: TemplateRef<unknown>;
  isTableLoading = true;
  private tableFilterSettingsSubscription?: Subscription;

  readonly departments = Array.from(
    DepartmentDescriptions.entries(),
    ([id, description]) => ({ id, description }),
  );
  readonly categories = Array.from(
    CategoryDescriptions.entries(),
    ([id, description]) => ({ id, description }),
  );
  readonly currentUserName = this.api.FullName || 'HRT Connect User';

  get canEditDocuments(): boolean {
    return this.api.CanEditFeature(ApplicationFeature.RepDocuments);
  }

  private get filteredDocuments(): UserDocumentView[] {
    const activeDocuments = this.showArchived
      ? this.documents
      : this.documents.filter((document) => !document.IsArchived);
    const query = this.searchText.trim().toLowerCase();
    return activeDocuments.filter((document) => {
      const department = DepartmentDescriptions.get(document.DepartmentId) ?? '';
      const category = CategoryDescriptions.get(document.CategoryId) ?? '';
      const matchesSearch = !query || [
        document.Title,
        document.Description,
        department,
        category,
        document.CreatedByUserName,
      ].some((value) => value.toLowerCase().includes(query));
      if (!matchesSearch) return false;

      const rowValues: Record<string, string> = {
        Title: document.Title,
        Description: document.Description,
        Department: department,
        Category: category,
        CreatedByUserName: document.CreatedByUserName,
        CreatedDate: document.CreatedDate
          ? new Date(document.CreatedDate).toLocaleDateString()
          : '',
      };
      return Object.entries(this.activeChipFilters.filters ?? {}).every(([key, settings]: [string, any]) => {
        const selectedValues = settings?.filter;
        if (!Array.isArray(selectedValues) || selectedValues.length === 0) return true;
        const cellValue = (rowValues[key] ?? '').toLowerCase();
        return selectedValues.some((selected: string) =>
          key === 'Department' || key === 'Category'
            ? cellValue === String(selected).toLowerCase()
            : cellValue.includes(String(selected).toLowerCase()),
        );
      });
    });
  }

  ngOnInit() {
    this.columnDefinitions = this.createColumnDefinitions();
      this.tableFilterSettingsSubscription = this.tableService
        .getFilterTableSettings(this.tableId)
        .subscribe((settings) => {
          this.page = settings?.page ?? 0;
          this.pageSize = settings?.pageSize ?? this.pageSize;
          this.activeChipFilters.filters = Object.fromEntries(
            Object.entries(settings.columns ?? {}).map(([key, settings]: [string, any]) => {
              const filterSettings = { ...settings };
              delete filterSettings.sort;
              return [key, filterSettings];
            }),
          );
          this.updateTableRows();
        });
    this.loadDocuments();
  }

  private createColumnDefinitions(): ColumnDefinitionV3[] {
    const columns: ColumnDefinitionV3[] = [
      {
        displayName: 'Title',
        key: 'Title',
        sortable: false,
        minWidth: 180,
        filter: { component: TextFilterV3Component },
      },
      {
        displayName: 'Description',
        key: 'Description',
        sortable: false,
        minWidth: 315,
        filter: { component: TextFilterV3Component },
        renderer: {
          component: RepDocumentsCellRenderer as ColumnRendererComponentV3<any>,
          config: { type: 'description' },
        },
      },
      {
        displayName: 'Department',
        key: 'Department',
        sortable: false,
        minWidth: 180,
        filter: {
          component: SelectFilterV3Component,
          config: {
            options: this.departments.map((department) => ({
              name: department.description,
              value: department.description,
            })),
          },
        } as SelectFilterRendererV3,
      },
      {
        displayName: 'Category',
        key: 'Category',
        sortable: false,
        minWidth: 180,
        filter: {
          component: SelectFilterV3Component,
          config: {
            options: this.categories.map((category) => ({
              name: category.description,
              value: category.description,
            })),
          },
        } as SelectFilterRendererV3,
      },
      {
        displayName: 'Created By',
        key: 'CreatedByUserName',
        sortable: false,
        minWidth: 160,
        filter: { component: TextFilterV3Component },
      },
      {
        displayName: 'Created Date',
        key: 'CreatedDate',
        sortable: false,
        minWidth: 130,
        filter: { component: TextFilterV3Component },
      },
      {
        displayName: 'File Type',
        key: 'FileType',
        sortable: false,
        minWidth: 90,
        renderer: {
          component: RenderFileIcon as ColumnRendererComponentV3<any>,
        },
      },
      {
        displayName: 'Actions',
        key: 'no_key_actions',
        sortable: false,
        resizeable: false,
        width: 120,
        minWidth: 120,
        renderer: {
          component: RepDocumentsCellRenderer as ColumnRendererComponentV3<any>,
          config: {
            type: 'actions',
            canEdit: this.canEditDocuments,
            onDownload: (row: any) => this.downloadDocument(row.Document),
            onEdit: (row: any) => this.openEditDocument(row.Document, this.documentDialog),
          },
        },
      },
    ];
    return columns;
  }

  private updateTableRows() {
    const rows = this.filteredDocuments.map((document) => ({
      Title: document.Title,
      Description: document.Description,
      Department: this.departmentName(document.DepartmentId),
      Category: this.categoryName(document.CategoryId),
      CreatedByUserName: document.CreatedByUserName,
      CreatedDate: document.CreatedDate
        ? new Date(document.CreatedDate).toLocaleDateString()
        : '',
      FileType: this.getDocumentFileType(document.Filename),
      Document: document,
    }));
    this.totalRows = rows.length;
    const lastPage = Math.max(0, Math.ceil(rows.length / this.pageSize) - 1);
    const page = Math.min(this.page, lastPage);
    this.tableRows = rows.slice(page * this.pageSize, (page + 1) * this.pageSize);
  }

  private resetToFirstPage() {
    this.page = 0;
    this.tableService.updateFilterTableSettings(this.tableId, { columns: {}, page: 0 } as any);
  }

  searchValueChanged(value: string) {
    this.searchText = value ?? '';
    this.activeChipFilters.searchText = this.searchText;
    this.resetToFirstPage();
    this.updateTableRows();
  }

  clearSearch() {
    this.searchText = '';
    this.activeChipFilters.searchText = '';
    this.searchForm.get('search')?.setValue('');
    this.resetToFirstPage();
    this.updateTableRows();
  }

  onChipRemoved(chip: Chip) {
    if (chip.chipKey === 'clearAll') {
      this.clearSearch();
      const clearedColumns = Object.fromEntries(
        Object.keys(this.activeChipFilters.filters ?? {}).map((key) => [key, { filter: [] }]),
      );
      this.tableService.updateFilterTableSettings(this.tableId, { columns: clearedColumns } as any);
      return;
    }

    if (chip.chipKey === 'searchText') {
      this.clearSearch();
      return;
    }

    const columnFilter = this.activeChipFilters.filters?.[chip.chipKey];
    if (Array.isArray(columnFilter?.filter)) {
      const updatedColumns = {
        ...this.activeChipFilters.filters,
        [chip.chipKey]: {
          ...columnFilter,
          filter: columnFilter.filter.filter((value: string) => value !== chip.chipValue),
        },
      };
      this.tableService.updateFilterTableSettings(this.tableId, { columns: updatedColumns } as any);
    }
  }

  setShowArchived(showArchived: boolean) {
    this.showArchived = showArchived;
    this.resetToFirstPage();
    this.updateTableRows();
  }

  loadDocuments() {
    this.isTableLoading = true;
    this.api.Documents.GetUserDocuments().subscribe({
      next: (documents) => {
        this.documents = documents ?? [];
        this.pageMessage = '';
        this.updateTableRows();
        this.isTableLoading = false;
      },
      error: () => {
        this.pageMessage = 'Unable to load Rep Documents.';
        this.isTableLoading = false;
      },
    });
  }

  openAddDocument(template: TemplateRef<unknown>, file: File | null = null) {
    if (!this.canEditDocuments || this.isDocumentDialogOpen) return;
    this.draft = Object.assign(new UserDocumentView(), {
      CreatedByUserId: this.api.UserId,
      CreatedDate: new Date().toISOString(),
      DepartmentId: Department.Sales,
      CategoryId: Category.ProductUpdates,
    });
    this.setSelectedFile(file);
    this.openDocumentDialog(template);
  }

  onPageFilesDropped(files: FileList, template: TemplateRef<unknown>) {
    if (!this.canEditDocuments) return;
    const file = files.item(0);
    if (!file) return;
    if (this.isDocumentDialogOpen) {
      this.onFilesDropped(files);
      return;
    }
    this.openAddDocument(template, file);
  }

  @HostListener('document:dragover', ['$event'])
  onDocumentDragOver(event: DragEvent) {
    if (this.isDocumentDialogOpen) event.preventDefault();
  }

  @HostListener('document:drop', ['$event'])
  onDocumentDrop(event: DragEvent) {
    if (!this.isDocumentDialogOpen || !this.canEditDocuments) return;
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files?.length) this.onFilesDropped(files);
  }

  private openDocumentDialog(template: TemplateRef<unknown>) {
    const dialogRef = this.dialog.open(template, {
      width: '680px',
      maxWidth: '95vw',
      minHeight: 'min(736px, calc(100vh - 32px))',
      maxHeight: 'calc(100vh - 24px)',
      disableClose: true,
    });
    this.dialogRef = dialogRef;
    this.isDocumentDialogOpen = true;
    dialogRef.afterClosed().subscribe(() => {
      if (this.dialogRef === dialogRef) {
        this.dialogRef = null;
        this.isDocumentDialogOpen = false;
      }
    });
  }

  openEditDocument(document: UserDocument, template: TemplateRef<unknown>) {
    if (!this.canEditDocuments) return;
    this.draft = Object.assign(new UserDocumentView(), structuredClone(document));
    this.selectedFile = null;
    this.openDocumentDialog(template);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.setSelectedFile(input.files?.[0] ?? null);
  }

  onFilesDropped(files: FileList) {
    this.setSelectedFile(files.item(0));
  }

  private setSelectedFile(file: File | null) {
    this.selectedFile = file;
    if (file) {
      this.draft.Filename = file.name;
      if (!this.draft.Title.trim()) {
        const extensionIndex = file.name.lastIndexOf('.');
        const title = extensionIndex > 0 ? file.name.slice(0, extensionIndex) : file.name;
        this.draft.Title = title.replace(/_/g, ' ');
      }
    }
  }

  saveDocument() {
    if (!this.canEditDocuments || !this.draft.Title.trim() || this.draft.DepartmentId == null || this.isSaving) return;
    if (!this.draft.Id && !this.selectedFile) {
      this.dialogMessage = 'Select a document to upload.';
      return;
    }

    this.isSaving = true;
    if (this.selectedFile) this.draft.Filename = this.selectedFile.name;
    this.dialogMessage = '';
    if (this.draft.Id) {
      this.api.Documents.UpdateUserDocument(this.draft).subscribe({
        next: () => this.uploadOrFinish(this.draft.Id),
        error: () => this.handleSaveError('Unable to save the document details.'),
      });
    } else {
      this.api.Documents.CreateUserDocument(this.draft).subscribe({
        next: (documentId) => this.uploadOrFinish(Number(documentId)),
        error: () => this.handleSaveError('Unable to save the document details.'),
      });
    }
  }

  cancelEdit() {
    if (!this.isSaving) this.dialogRef?.close();
  }

  departmentName(departmentId: number): string {
    return DepartmentDescriptions.get(departmentId) ?? '';
  }

  categoryName(categoryId: number): string {
    return CategoryDescriptions.get(categoryId) ?? '';
  }

  private getDocumentFileType(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase() ?? '';
    switch (extension) {
      case 'pdf': return 'PDF';
      case 'dwg': return 'DWG';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'tif':
      case 'tiff': return 'JPEG';
      case 'zip': return 'ZIP';
      case 'mp4':
      case 'mov':
      case 'avi':
      case 'wmv': return 'Video';
      case 'step': return 'STEP';
      case 'stp': return 'stp';
      case 'sat': return 'sat';
      case 'ipt': return 'ipt';
      default: return 'Document';
    }
  }

  descriptionPreview(description: string | null | undefined): string {
    if (!description || description.length <= 50) return description ?? '';
    return description.slice(0, 50) + '...';
  }

  downloadDocument(document: UserDocument) {
    this.pageMessage = '';
    this.api.Documents.DownloadUserDocument(document, (error) => {
      if (error) this.pageMessage = error;
    });
  }

  private finishSave() {
    this.isSaving = false;
    this.dialogRef?.close();
    this.loadDocuments();
  }

  private uploadOrFinish(documentId: number) {
    if (!Number.isSafeInteger(documentId) || documentId <= 0) {
      this.handleSaveError('The document was saved, but its ID could not be read for upload.');
      return;
    }
    this.draft.Id = documentId;
    if (!this.selectedFile) {
      this.finishSave();
      return;
    }
    this.api.Documents.UploadUserDocument(documentId, [this.selectedFile]).subscribe({
      next: () => this.finishSave(),
      error: () => this.handleSaveError('Unable to upload the document.'),
    });
  }

  private handleSaveError(message: string) {
    this.isSaving = false;
    this.dialogMessage = message;
  }

  ngOnDestroy() {
    this.tableFilterSettingsSubscription?.unsubscribe();
  }
}